import { type ReactNode } from 'react';

/* ── Full empty state — centred in the content area ── */

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-20) var(--space-6)',
      textAlign: 'center',
      maxWidth: '360px',
      margin: '0 auto',
    }}>
      {icon && (
        <div style={{
          width: 'var(--space-14)',
          height: 'var(--space-14)',
          borderRadius: 'var(--radius-full)',
          backgroundColor: 'var(--color-surface-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 'var(--space-5)',
          color: 'var(--color-text-subtle)',
        }}>
          {icon}
        </div>
      )}

      <h3 style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-lg)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--color-text-primary)',
        marginBottom: description ? 'var(--space-2)' : action ? 'var(--space-5)' : 0,
        lineHeight: 'var(--leading-snug)',
      }}>
        {title}
      </h3>

      {description && (
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-md)',
          color: 'var(--color-text-muted)',
          lineHeight: 'var(--leading-relaxed)',
          marginBottom: action ? 'var(--space-6)' : 0,
        }}>
          {description}
        </p>
      )}

      {action}
    </div>
  );
}

/* ── Compact / inline empty state — inside a card or table ── */

interface CompactEmptyStateProps {
  icon?: ReactNode;
  message: string;
  action?: ReactNode;
}

export function CompactEmptyState({ icon, message, action }: CompactEmptyStateProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-3)',
      padding: 'var(--space-4)',
      backgroundColor: 'var(--color-surface-muted)',
      borderRadius: 'var(--radius-input)',
      border: '1px dashed var(--color-border-strong)',
    }}>
      {icon && (
        <span style={{ color: 'var(--color-text-subtle)', flexShrink: 0 }}>
          {icon}
        </span>
      )}
      <span style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-muted)',
        flex: 1,
      }}>
        {message}
      </span>
      {action}
    </div>
  );
}
